/**
 * document-store.ts
 * Where "Mis documentos" keeps what the worker already loaded, so it opens
 * with no signal (operator-offline-documents): the last list per user, and
 * the files the worker opened, keyed by `${owner}:${fileId}`. A fileId names
 * one immutable upload (replacing a document's file gives it a new fileId),
 * so it is a stable version key — never the URL. Same posture as
 * dictation-store.ts: plain IndexedDB (only it stores Blobs and survives the
 * PWA being closed), its own database so neither upgrade blocks the other.
 *
 * `DocumentStore` is the seam offline-documents.ts talks to, so its tests run
 * against an in-memory store (jsdom has no IndexedDB).
 */
import type { SelfEmployeeDocumentResponse } from '@suite/contracts-rrhh';

export interface CachedDocumentList {
  /** `${tenantId}:${sub}`; a shared phone never mixes users. */
  readonly owner: string;
  readonly items: SelfEmployeeDocumentResponse[];
  /** When the server answered it (ISO), shown with no signal. */
  readonly savedAt: string;
}

export interface CachedDocumentFile {
  /** `${owner}:${fileId}`. */
  readonly key: string;
  readonly owner: string;
  readonly fileId: string;
  readonly blob: Blob;
  readonly size: number;
  /** Last time it was opened (ISO): the least recent goes first when full. */
  readonly lastOpenedAt: string;
}

export interface DocumentStore {
  getList(owner: string): Promise<CachedDocumentList | undefined>;
  putList(row: CachedDocumentList): Promise<void>;
  files(): Promise<CachedDocumentFile[]>;
  getFile(key: string): Promise<CachedDocumentFile | undefined>;
  putFile(row: CachedDocumentFile): Promise<void>;
  removeFile(key: string): Promise<void>;
  clear(): Promise<void>;
}

const DB_NAME = 'rigx-offline-documents';
const DB_VERSION = 1;
const LISTS = 'lists';
const FILES = 'files';

function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  dbPromise ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(LISTS)) db.createObjectStore(LISTS, { keyPath: 'owner' });
      if (!db.objectStoreNames.contains(FILES)) db.createObjectStore(FILES, { keyPath: 'key' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      dbPromise = null;
      reject(req.error);
    };
  });
  return dbPromise;
}

async function withStore<T>(
  name: string,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb();
  return promisify(run(db.transaction(name, mode).objectStore(name)));
}

export const indexedDbDocumentStore: DocumentStore = {
  getList: (owner) => withStore(LISTS, 'readonly', (s) => s.get(owner) as IDBRequest<CachedDocumentList | undefined>),
  putList: async (row) => {
    await withStore(LISTS, 'readwrite', (s) => s.put(row));
  },
  files: () => withStore(FILES, 'readonly', (s) => s.getAll() as IDBRequest<CachedDocumentFile[]>),
  getFile: (key) => withStore(FILES, 'readonly', (s) => s.get(key) as IDBRequest<CachedDocumentFile | undefined>),
  putFile: async (row) => {
    await withStore(FILES, 'readwrite', (s) => s.put(row));
  },
  removeFile: async (key) => {
    await withStore(FILES, 'readwrite', (s) => s.delete(key));
  },
  clear: async () => {
    await withStore(LISTS, 'readwrite', (s) => s.clear());
    await withStore(FILES, 'readwrite', (s) => s.clear());
  },
};
